"use client";

/**
 * ScaleEfficiency — the two constraints that usually decide whether an index can exist at all,
 * and what askFinz spends on each. One card, two columns: compute on the left, storage on the right.
 *
 * WHY THESE TWO TOGETHER. Asked why they are not building their own index, most teams give one of
 * exactly two answers: we cannot afford the compute, or we cannot afford to keep the data. Both are
 * assumptions about scale that come from watching frontier labs, and both are answered here with
 * askFinz's own numbers. Separating them across two sections would let a reader accept one and
 * carry the other objection to the end of the page; side by side they are one argument.
 *
 * WHAT IS LIVE AND WHAT IS FIXED, because the distinction matters more than the numbers.
 *  - The storage column is LIVE. Bytes per stored page comes from the running system, so the
 *    "fits on a memory card" arithmetic recomputes itself and cannot drift into a lie the way a
 *    hardcoded ratio would. The reference rate is imported from StorageScaleExplorer rather than
 *    copied, so /scale and /compare/web-index cannot quote different ratios.
 *  - The compute column is FIXED, and deliberately so. A GPU's specification and its launch price
 *    are facts about 2016 that will never change; publishing them needs no live feed and carries
 *    no risk of going stale.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. It does not name a specific current accelerator or quote its
 * throughput. Datacentre part numbers, their performance figures and their prices move constantly,
 * and a marketing page that states one incorrectly hands a technical reader a reason to distrust
 * everything around it. The comparison is made on PRICE CLASS, at a published list range for a
 * populated 8-GPU server, which is both stable enough to publish and the number that actually
 * carries the argument. The point was never that our GPUs are fast.
 *
 * THE STORAGE COMPARISON IS NOT LIKE-FOR-LIKE and says so. The reference is a public raw archive of
 * fetched pages; askFinz stores processed passages plus their vectors. Different artefacts for
 * different purposes, and part of the gap is that difference rather than engineering merit. It is
 * used because it is the only web-scale per-page figure anyone can verify independently.
 */

import { useEffect, useState } from "react";
import Link from "next/link";
import { Cpu, HardDrive } from "lucide-react";
import { fmtCount } from "@/lib/format-stat";
import { CC_BYTES_PER_PAGE, CC_LABEL } from "@/components/marketing/storage-scale-explorer";

/** A 32 GB memory card — the smallest capacity most readers have physically held. */
const CARD_BYTES = 32 * 1024 ** 3;
const CARD_LABEL = "32 GB memory card";

/* The GPU actually doing the reading. 2016, Pascal, consumer, and long out of production — every
   figure here is a published specification that cannot go stale. */
const OURS = [
  { k: "Architecture", v: "Pascal, 2016" },
  { k: "Compute", v: "4.37 TFLOPS" },
  { k: "Memory", v: "6 GB GDDR5" },
  { k: "Launch price", v: "about €438" },
];

/* Where the rest of the industry starts. Price class only — see the header note on why there is no
   part number and no throughput figure here. */
const THEIRS = [
  { k: "Architecture", v: "Current generation" },
  { k: "Per accelerator", v: "€35,000–40,000" },
  { k: "Populated 8-GPU server", v: "€250,000–400,000" },
  { k: "Housed in", v: "A datacentre" },
];

/* Every machine class the indexer node actually runs on. The single-board computer is the one that
   makes the point: the software was written to fit whatever hardware exists, rather than the
   hardware being bought to fit the software. */
const RUNS_ON = [
  "A Raspberry Pi 4B",
  "A laptop",
  "A Mac mini",
  "A rack server",
  "x86 or ARM",
];

export function ScaleEfficiency(
  { initial }: { initial?: { perPage?: number | null } | null } = {},
) {
  const [perPage, setPerPage] = useState<number | null>(initial?.perPage ?? null);

  useEffect(() => {
    let on = true;
    const load = async () => {
      try {
        const r = await fetch("/api/index/storage", { cache: "no-store" });
        if (!r.ok) return;
        const j = (await r.json()) as { web?: { bytes_per_page_disk?: number; bytes_per_page?: number } };
        const bpp = j?.web?.bytes_per_page_disk ?? j?.web?.bytes_per_page;
        if (on && typeof bpp === "number" && bpp > 0) setPerPage(bpp);
      } catch {
        /* transient — keep whatever is on screen */
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      on = false;
      clearInterval(t);
    };
  }, []);

  const oursOnCard = perPage ? Math.floor(CARD_BYTES / perPage) : null;
  const theirsOnCard = Math.floor(CARD_BYTES / CC_BYTES_PER_PAGE);
  const ratio = perPage ? CC_BYTES_PER_PAGE / perPage : null;
  const perPageLabel = perPage
    ? perPage >= 1000
      ? `${Math.round(perPage / 1000)} KB`
      : `${Math.round(perPage)} B`
    : "—";

  return (
    <div className="grid gap-px overflow-hidden rounded-2xl border hairline-strong bg-foreground/10 md:grid-cols-2">
      {/* ── Compute ───────────────────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-5 bg-card p-7 md:p-9">
        <div className="flex items-center gap-2">
          <Cpu className="h-4 w-4 shrink-0 text-foreground/50" aria-hidden />
          <span className="kicker">Compute</span>
        </div>
        <h3 className="display-text is-stat text-[clamp(1.35rem,2.2vw,1.75rem)] leading-[1.14]">
          The index was built on a graphics card from 2016.
        </h3>
        <p className="text-[14.5px] leading-relaxed text-foreground/65">
          Not on a frontier cluster, not on reserved cloud capacity, and not in a datacentre. The
          machines that read the web for askFinz run consumer hardware that was mid-range when it
          was new and is a decade old now.
        </p>

        <div className="grid gap-px overflow-hidden rounded-xl border hairline bg-foreground/10 sm:grid-cols-2">
          <div className="flex flex-col gap-2.5 bg-card p-4">
            <span className="kicker opacity-80">What we run</span>
            <dl className="flex flex-col gap-1.5">
              {OURS.map((r) => (
                <div key={r.k} className="flex items-baseline justify-between gap-3">
                  <dt className="text-[12px] text-foreground/50">{r.k}</dt>
                  <dd className="text-[13px] font-medium tabular-nums">{r.v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="flex flex-col gap-2.5 bg-card p-4">
            <span className="kicker opacity-80">What the industry starts at</span>
            <dl className="flex flex-col gap-1.5">
              {THEIRS.map((r) => (
                <div key={r.k} className="flex items-baseline justify-between gap-3">
                  <dt className="text-[12px] text-foreground/50">{r.k}</dt>
                  <dd className="text-[13px] font-medium tabular-nums text-foreground/70">{r.v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <span className="kicker opacity-80">And it runs on</span>
          <ul className="flex flex-wrap gap-1.5">
            {RUNS_ON.map((r) => (
              <li
                key={r}
                className="rounded-full border hairline px-2.5 py-1 text-[12px] text-foreground/65"
              >
                {r}
              </li>
            ))}
          </ul>
        </div>

        <p className="text-[13.5px] leading-relaxed text-foreground/60">
          A reading node is a normal computer, so capacity grows by adding ordinary machines to a
          pool rather than by buying a rack.{" "}
          <Link
            href="/indexer_os"
            className="text-foreground/80 underline underline-offset-4 transition-colors hover:text-foreground"
          >
            How a node works
          </Link>{" "}
          ·{" "}
          <Link
            href="/infrastructure"
            className="text-foreground/80 underline underline-offset-4 transition-colors hover:text-foreground"
          >
            The infrastructure
          </Link>
        </p>
      </div>

      {/* ── Storage ───────────────────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-5 bg-card p-7 md:p-9">
        <div className="flex items-center gap-2">
          <HardDrive className="h-4 w-4 shrink-0 text-foreground/50" aria-hidden />
          <span className="kicker">Storage</span>
        </div>
        <h3 className="display-text is-stat text-[clamp(1.35rem,2.2vw,1.75rem)] leading-[1.14]">
          A stored page costs {perPageLabel}.
        </h3>
        <p className="text-[14.5px] leading-relaxed text-foreground/65">
          That is the cleaned text and the vectors, with the markup, scripts and navigation
          furniture thrown away. It is the figure that decides whether an index can keep growing on
          hardware instead of on a balance sheet — so it is read live from the running system rather
          than typed here.
        </p>

        <div className="flex flex-col gap-3 rounded-xl border hairline p-5">
          <span className="kicker opacity-80">On one {CARD_LABEL}</span>
          <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-4">
              <span className="text-[13px] text-foreground/60">Filed by askFinz</span>
              <span className="display-text is-stat tabular-num whitespace-nowrap text-[26px]">
                {oursOnCard ? fmtCount(oursOnCard) : "—"}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
              <div className="h-full rounded-full bg-[hsl(var(--accent))]" style={{ width: "100%" }} />
            </div>
            <div className="flex items-baseline justify-between gap-4">
              <span className="text-[13px] text-foreground/60">
                At the reference rate of {CC_LABEL} a page
              </span>
              <span className="display-text is-stat tabular-num whitespace-nowrap text-[26px] text-foreground/50">
                {fmtCount(theirsOnCard)}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
              <div
                className="h-full rounded-full bg-foreground/30"
                style={{ width: ratio ? `${Math.max(2, 100 / ratio)}%` : "12%" }}
              />
            </div>
          </div>
          <p className="text-[12.5px] leading-relaxed text-foreground/50">
            {ratio
              ? `About ${ratio.toFixed(1)}× more pages on the same card. `
              : ""}
            Not like for like: the reference is a raw archive of fetched pages, while askFinz stores
            processed passages and their vectors. Part of the gap is that difference rather than
            engineering.
          </p>
        </div>

        <p className="text-[13.5px] leading-relaxed text-foreground/60">
          Drag the capacity up to a drive or a rack and the same arithmetic holds.{" "}
          <Link
            href="/compare/web-index"
            className="text-foreground/80 underline underline-offset-4 transition-colors hover:text-foreground"
          >
            The full comparison, with the workings
          </Link>{" "}
          ·{" "}
          <Link
            href="/how-the-index-works"
            className="text-foreground/80 underline underline-offset-4 transition-colors hover:text-foreground"
          >
            How a page is filed
          </Link>
        </p>
      </div>
    </div>
  );
}
